import { useState } from 'react';
import { DocumentNode, useMutation } from '@apollo/client';

import { describeEditError, forgetEditData } from './contactEdit';

/**
 * Runs one edit mutation (card 21.5): drops what it changes from the cache,
 * calls `onDone` when it worked, and keeps the error in words when it did not.
 * `run` resolves with `ok`, and the error when there is one, so a dialog knows
 * whether to close and a one-click edit can say why it failed.
 */
export const useContactEdit = (mutation: DocumentNode, onDone?: () => void) => {
  const [mutate, { loading }] = useMutation(mutation, { update: (cache) => forgetEditData(cache) });
  const [error, setError] = useState<{ message: string; field?: string } | null>(null);

  const run = async (variables: Record<string, unknown>): Promise<{ ok: boolean; error?: { message: string; field?: string } }> => {
    setError(null);
    try {
      await mutate({ variables });
      onDone?.();
      return { ok: true };
    } catch (err) {
      const described = describeEditError(err as Error);
      setError(described);
      return { ok: false, error: described };
    }
  };

  return { run, loading, error, clearError: () => setError(null) };
};
